import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as tasksApi from './api'
import type {
  AddCommentInput,
  ChangeAssigneeInput,
  ContextBoardKey,
  CreateActionTypeInput,
  CreateBoardColumnInput,
  CreateDocumentTypeInput,
  CreateTaskInput,
  TaskContext,
  TaskStatus,
  UpdateActionTypeInput,
  UpdateBoardColumnInput,
  UpdateDocumentTypeInput,
  UpdateFileMetadataInput,
  UploadFileInput,
} from './types'

export const taskKeys = {
  my: (status?: TaskStatus) => ['tasks', 'my', status ?? 'all'] as const,
  detail: (id: number) => ['tasks', id] as const,
}

export function useMyTasks(status?: TaskStatus) {
  return useQuery({
    queryKey: taskKeys.my(status),
    queryFn: () => tasksApi.listMyTasks(status),
  })
}

export function useTask(id: number | null | undefined) {
  return useQuery({
    queryKey: id != null ? taskKeys.detail(id) : ['tasks', 'disabled'],
    queryFn: () => tasksApi.getTask(id!),
    enabled: id != null,
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTaskInput) => tasksApi.createTask(input),
    onSuccess: () => {
      // Invaliduj OBA - ['tasks'] dla list, ['boards'] dla kanban (personal + context).
      // ContractTasksTab uzywa useContextBoard -> bez ['boards'] nowy task nie pojawia
      // sie do hard refresh.
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
    },
  })
}

export function useChangeTaskStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: TaskStatus }) =>
      tasksApi.changeTaskStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
    },
  })
}

// Faza 2A - delegacja
export function useClaimTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => tasksApi.claimTask(id),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
      qc.setQueryData(taskKeys.detail(task.id), task)
    },
  })
}

export function useUnclaimTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => tasksApi.unclaimTask(id),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
      qc.setQueryData(taskKeys.detail(task.id), task)
    },
  })
}

export function useChangeAssignee(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ChangeAssigneeInput) => tasksApi.changeAssignee(id, input),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })  // kanban refresh - bez tego kanban nie reaguje bez hard refresh
      qc.setQueryData(taskKeys.detail(task.id), task)
    },
  })
}

// Faza 2B - kanban / boards

export const boardKeys = {
  personal: () => ['boards', 'personal'] as const,
  context: (k: ContextBoardKey) =>
    ['boards', 'context', k.system, k.type, k.id] as const,
}

export function usePersonalBoard() {
  return useQuery({
    queryKey: boardKeys.personal(),
    queryFn: tasksApi.getPersonalBoard,
  })
}

export function useContextBoard(key: ContextBoardKey | null) {
  return useQuery({
    queryKey: key ? boardKeys.context(key) : ['boards', 'context', 'disabled'],
    queryFn: () => tasksApi.getContextBoard(key!),
    enabled: key != null,
  })
}

export function useCreateBoardColumn(boardId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateBoardColumnInput) => tasksApi.createBoardColumn(boardId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards'] }),
  })
}

export function useUpdateBoardColumn(boardId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { columnId: number; patch: UpdateBoardColumnInput }) =>
      tasksApi.updateBoardColumn(boardId, vars.columnId, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards'] }),
  })
}

export function useDeleteBoardColumn(boardId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { columnId: number; moveCardsToColumnId?: number }) =>
      tasksApi.deleteBoardColumn(boardId, vars.columnId, vars.moveCardsToColumnId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards'] }),
  })
}

/**
 * Drag&drop mutation. Frontend wywoluje natychmiast po dropie - na sukces backend
 * zwraca swiezy snapshot boardu, ktorym wymieniamy cache. Optimistic update w
 * onMutate (patrz KanbanBoard).
 */
export function useMoveCard(boardId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { taskId: number; columnId: number; position: number }) =>
      tasksApi.moveCard(boardId, vars.taskId, vars.columnId, vars.position),
    onSuccess: () => {
      // Invalidate wszystkie boardy (status moze sie zmienic cross-board) + listy task'ow
      qc.invalidateQueries({ queryKey: ['boards'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

// Faza 2C - comments

export const commentKeys = {
  forTask: (taskId: number) => ['tasks', taskId, 'comments'] as const,
}

export function useTaskComments(taskId: number | null) {
  return useQuery({
    queryKey: taskId != null ? commentKeys.forTask(taskId) : ['tasks', 'comments', 'disabled'],
    queryFn: () => tasksApi.listComments(taskId!),
    enabled: taskId != null,
  })
}

export function useAddComment(taskId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AddCommentInput) => tasksApi.addComment(taskId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentKeys.forTask(taskId) }),
  })
}

// Faza 2D - pliki zadań

export const fileKeys = {
  forTask: (taskId: number) => ['tasks', taskId, 'files'] as const,
  documentTypes: () => ['task-document-types'] as const,
}

export function useTaskFiles(taskId: number | null) {
  return useQuery({
    queryKey: taskId != null ? fileKeys.forTask(taskId) : ['tasks', 'files', 'disabled'],
    queryFn: () => tasksApi.listTaskFiles(taskId!),
    enabled: taskId != null,
  })
}

export function useUploadTaskFile(taskId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UploadFileInput) => tasksApi.uploadTaskFile(taskId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.forTask(taskId) }),
  })
}

export function useDeleteTaskFile(taskId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: number) => tasksApi.deleteTaskFile(taskId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.forTask(taskId) }),
  })
}

export function useUpdateTaskFile(taskId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { fileId: number; patch: UpdateFileMetadataInput }) =>
      tasksApi.updateTaskFile(taskId, vars.fileId, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.forTask(taskId) }),
  })
}

export function useTaskDocumentTypes() {
  return useQuery({
    queryKey: fileKeys.documentTypes(),
    queryFn: tasksApi.listTaskDocumentTypes,
    staleTime: 5 * 60 * 1000,  // 5 min - typy dokumentow rzadko sie zmieniaja
  })
}

export function useCreateTaskDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDocumentTypeInput) => tasksApi.createTaskDocumentType(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.documentTypes() }),
  })
}

export function useUpdateTaskDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; patch: UpdateDocumentTypeInput }) =>
      tasksApi.updateTaskDocumentType(vars.code, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.documentTypes() }),
  })
}

export function useDeactivateTaskDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => tasksApi.deactivateTaskDocumentType(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.documentTypes() }),
  })
}

// Faza 2E - typy akcji

export const actionTypeKeys = {
  all: () => ['task-action-types'] as const,
}

export function useTaskActionTypes() {
  return useQuery({
    queryKey: actionTypeKeys.all(),
    queryFn: tasksApi.listTaskActionTypes,
    staleTime: 5 * 60 * 1000,  // 5 min - slownik rzadko sie zmienia
  })
}

export function useCreateTaskActionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateActionTypeInput) => tasksApi.createTaskActionType(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionTypeKeys.all() }),
  })
}

export function useUpdateTaskActionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; patch: UpdateActionTypeInput }) =>
      tasksApi.updateTaskActionType(vars.code, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionTypeKeys.all() }),
  })
}

export function useDeactivateTaskActionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => tasksApi.deactivateTaskActionType(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionTypeKeys.all() }),
  })
}

export function useChangeTaskActionType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { taskId: number; actionTypeCode: string | null }) =>
      tasksApi.changeTaskActionType(vars.taskId, vars.actionTypeCode),
    onSuccess: () => {
      // Invaliduj OBA - drawer/lista pod ['tasks'], kanban board pod ['boards'].
      // Bez ['boards'] kanban nie reaguje na zmiane do hard refresh.
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
    },
  })
}

// Faza 2H-B - zmiana kontekstu

export function useChangeTaskContext() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { taskId: number; context: TaskContext | null }) =>
      tasksApi.changeTaskContext(vars.taskId, vars.context),
    onSuccess: () => {
      // Invaliduj OBA - drawer/lista pod ['tasks'], kanban board pod ['boards'].
      // Kontekst jest widoczny na kartach kanban - bez ['boards'] kanban nie odswiezyl by sie.
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['boards'] })
    },
  })
}
